/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useRef, useState } from "react";

import { loginUserSessionService } from "../../services/userService"

// reactstrap components
// Only the grid is still Argon's. The card, fields and buttons are now the
// brand-auth primitives — see styles/brand-auth.css.
import { Container, Row, Col } from "reactstrap";
import "../../styles/brand-auth.css";
import { useNavigate } from "react-router-dom";
// core components
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import { useUserAuth } from "../../context/UserContext";
import { useLesson } from "../../hooks/lessonFramework";
import useReveal from "hooks/useReveal";
import { useTranslation } from "react-i18next";
import logoVideo from "../../assets/videos/loadingCircleBlackOnWhite1080.mp4";

// The looping logo is decoration. Visitors who ask for less motion get the
// loading text without it.
const prefersReducedMotion = () =>
  Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

// Firebase reset error codes, mapped to translation keys. Shared keys are
// reused where the wording is the same.
const RESET_ERROR_KEYS = {
  "auth/invalid-email": "register.errors.invalidEmail",
  "auth/missing-email": "forgot.errors.missingEmail",
  // Many apps prefer not to reveal if an email exists.
  "auth/user-not-found": "forgot.sentIfExists",
  "auth/user-disabled": "login.errors.userDisabled",
  "auth/too-many-requests": "register.errors.tooManyRequests",
  "auth/network-request-failed": "register.errors.network",
  "auth/operation-not-allowed": "forgot.errors.notAllowed",
  "auth/unauthorized-continue-uri": "forgot.errors.setup",
  "auth/invalid-continue-uri": "forgot.errors.setup",
};

const ForgotPasswordComponent = () => {

  // The card's entrance — see styles/premium.css.
  useReveal();

  const [email, setEmail] = useState("");

  // const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const mainRef = useRef(null);
  const navigate = useNavigate();
  const { setUser, user, logIn, forgotPassword } = useUserAuth();
  const { t } = useTranslation();

  // OLD LOGIN
  // const { logIn } = useUserAuth();

  const [isLogingIn, setIsLogingIn] = useState(false);


  // Framework call example
  const { variable1, variable2, method1, method2 } = useLesson();




  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {

      setIsLogingIn(true);


      await forgotPassword(email.trim());

      // navigate("/home");
    } catch (error) {
      setError(getAuthErrorMessage(error));
      // setError(error.message);
    } finally {
      setIsLogingIn(false);
    }
  };


  const getAuthErrorMessage = (error) => {
    const code = error?.code || error?.errorCode;
    return t(RESET_ERROR_KEYS[code] || "forgot.errors.generic");
  };



  // OLD LOGIN. 
  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   setError("");
  //   try {
  //     const responseUser = await loginUser(email, password);
  //     setUser(responseUser)
  //     navigate("/home");
  //   } catch (error) {
  //     setError(error.message);
  //   }
  // };

  return (
    <>
      <DemoNavbar />
      <main className="cn-auth" ref={mainRef}>

        {isLogingIn && (
          <div className="courses-loading-overlay" role="alert" aria-busy="true" aria-live="polite">
            <div className="courses-loading-card">
              {!prefersReducedMotion() && (
                <video
                  src={logoVideo}
                  autoPlay
                  loop
                  muted
                  playsInline
                  draggable={false}
                  aria-hidden="true"
                  style={{ width: 72, height: 72, display: "block" }}
                />
              )}
              <div className="courses-loading-text">{t("studio.common.loading")}</div>
            </div>
          </div>
        )}

        <section className="cn-auth-hero cn-grid">
          <span className="cn-grain" aria-hidden="true" />
          <span className="cn-orb cn-orb-1" />
          <span className="cn-orb cn-orb-2" />
          <span className="cn-orb cn-orb-3" />
          <Container>
            <Row className="justify-content-center">
              <Col xs="12" md="10" lg="6" xl="5">
                <div className="cn-card" data-reveal>
                  <div className="cn-card-head">
                    <h1 className="cn-title">{t("login.forgotPassword")}</h1>
                    <p className="cn-sub">{t("forgot.subtitle")}</p>
                  </div>

                  <form onSubmit={handleSubmit} role="form">
                    <div className="cn-field cn-field-last">
                      <label className="cn-label" htmlFor="forgot-email">
                        {t("login.email")}
                      </label>
                      <div className="cn-input-wrap">
                        <i className="ni ni-email-83 cn-input-icon" aria-hidden="true" />
                        <input
                          id="forgot-email"
                          className="cn-input"
                          type="email"
                          autoComplete="email"
                          placeholder={t("login.email")}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                    </div>

                    {error && (
                      <div className="cn-error" role="alert">
                        {error}
                      </div>
                    )}

                    <button
                      className="cn-btn-primary cn-btn-block cn-btn-arrow"
                      type="submit"
                      disabled={isLogingIn}
                    >
                      {t("regular.submit")}
                    </button>
                  </form>
                </div>

                {/* The left link said "Login" but navigated to /register-page,
                    same as the right one. It goes to /login-page now. */}
                <div className="cn-links" data-reveal style={{ "--reveal-delay": "90ms" }}>
                  <button
                    className="cn-link"
                    type="button"
                    onClick={() => navigate("/login-page")}
                  >
                    {t("nav.login")}
                  </button>
                  <button
                    className="cn-link"
                    type="button"
                    onClick={() => navigate("/register-page")}
                  >
                    {t("nav.register")}
                  </button>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      </main>
      <MainFooter />
    </>
  );
};

export default ForgotPasswordComponent;
